/**
 * License plate validation utilities.
 * Supports Austrian and European license plate formats.
 */

import i18n from 'i18next';

export type LicensePlateValidationResult = {
    isValid: boolean;
    isAustrian: boolean | null;
    isEuropean: boolean | null;
    message: string;
    messageType: 'success' | 'warning' | 'error';
};

/**
 * Austrian license plate pattern.
 * Format: 1-2 district letters + separator (optional) + 1-5 numbers + 0-3 letters
 * Letter Q is excluded from all sequences.
 * Examples: W-12345X, G-123AB, SL-45678, KS-1A
 */
const AUSTRIAN_LICENSE_PLATE_PATTERN = /^[A-PR-Z]{1,2}[-\s]?[0-9]{1,5}[A-PR-Z]{0,3}$/i;

/**
 * European license plate pattern (general/permissive).
 * Covers common formats from Germany, France, Italy, Spain, Netherlands, etc.
 * Format: 1-3 letters + optional separator + alphanumeric combinations
 * Must contain at least one digit (pure letter combinations are rejected).
 * Examples: M-AB 1234 (DE), AB-123-CD (FR), 1234 ABC (ES)
 */
const EUROPEAN_LICENSE_PLATE_PATTERN = /^(?=.*\d)[A-Z]{1,3}[-\s]?[A-Z0-9]{1,4}[-\s]?[A-Z0-9]{0,5}$/i;

/**
 * Validates a license plate and returns detailed validation result.
 * Messages are translated via i18n.
 * Requires minimum 3 characters for validation feedback.
 */
export function validateLicensePlate(licensePlate: string | undefined | null): LicensePlateValidationResult {
    const cleanPlate = (licensePlate || '').trim().toUpperCase();

    // Empty input - no message yet, but valid for form state
    if (cleanPlate.length === 0) {
        return {
            isValid: true,
            isAustrian: null,
            isEuropean: null,
            message: '',
            messageType: 'warning',
        };
    }

    // Minimum 3 characters required before showing validation feedback
    if (cleanPlate.length < 3) {
        return {
            isValid: true,
            isAustrian: null,
            isEuropean: null,
            message: '',
            messageType: 'warning',
        };
    }

    // Check Austrian format first (highest priority for Austrian dealership)
    if (AUSTRIAN_LICENSE_PLATE_PATTERN.test(cleanPlate)) {
        return {
            isValid: true,
            isAustrian: true,
            isEuropean: true,
            message: '',
            messageType: 'success',
        };
    }

    // Check European format
    if (EUROPEAN_LICENSE_PLATE_PATTERN.test(cleanPlate)) {
        return {
            isValid: true,
            isAustrian: false,
            isEuropean: true,
            message: '',
            messageType: 'success',
        };
    }

    // Does not match known patterns - warn user but allow submission
    return {
        isValid: true,
        isAustrian: false,
        isEuropean: false,
        message: i18n.t('licensePlate.invalidWarning'),
        messageType: 'warning',
    };
}

